import React, {useCallback, useEffect} from 'react';
import {Stack} from '@chakra-ui/react';
import {useDispatch, useSelector} from 'react-redux';
import {useSwiper} from 'swiper/react';
import CustomRadio from '~/components/elements/CustomRadio';
import {
  DATA_CHECK_BOX,
  DATA_SELECT,
  NO_OPTION,
  POP_UP_PH_CHECKOUT_KEYS,
  QUESTIONS,
  QUESTIONS_PARAMS,
  YES_OPTION,
} from '~/constants/popup';
import {useInput} from '~/hooks';
import {RootState} from '~/redux/root-reducer';
import {MedicalQuestionnaireKey} from '~/redux/questionnaires/types';
import {BackAndNextSlide, PopupLayout, TitlePopup} from '~/pages/homes/pop-up/components';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Col, UiButton} from '~/components/elements';
import {omit} from 'lodash';

export const PopupThree = () => {
  const dispatch = useDispatch();
  const swiper = useSwiper();
  const slideKey = useSelector((state: RootState) => state.questionnaires.nextSlideKey);
  const answer = useSelector((state: RootState) => state.questionnaires.collectData);
  const isGoback = !!answer[slideKey as MedicalQuestionnaireKey];

  const questionnaire = answer?.[POP_UP_PH_CHECKOUT_KEYS.MORE_INFORMATION];
  const {data: answerData} = questionnaire || {};

  const defaultValue = {
    breastFeedingOptions: answerData?.breastFeedingOptions || '',
    smokeOptions: answerData?.smokeOptions || '',
    oftenSmoke: answerData?.oftenSmoke || '',
  };

  const {inputs, setInputs} = useInput(defaultValue);

  const isNextSlide =
    inputs.smokeOptions === NO_OPTION
      ? Object.values(omit(inputs, ['oftenSmoke'])).every(el => !!el)
      : Object.values(inputs).every(el => !!el);

  const handleNextSlide = useCallback(() => {
    const data = inputs.smokeOptions === NO_OPTION ? omit(inputs, ['oftenSmoke']) : inputs;
    dispatch(
      setQuestionnaireAnswer({
        key: POP_UP_PH_CHECKOUT_KEYS.MORE_INFORMATION,
        index: 'SCREEN 3',
        data,
        questions: QUESTIONS_PARAMS.MORE_INFORMATION,
      }),
    );

    setTimeout(() => {
      swiper.slideNext();
    }, 200);
  }, [dispatch, inputs, swiper]);

  useEffect(() => {
    if (slideKey === POP_UP_PH_CHECKOUT_KEYS.MORE_INFORMATION) {
      if (isNextSlide) {
        swiper.allowSlideNext = true;
        if (isGoback) return;
        handleNextSlide();
      } else {
        swiper.allowSlideNext = false;
      }
    }
  }, [handleNextSlide, isGoback, isNextSlide, slideKey, swiper]);

  const handleClickOptions = (value: string, question: string) => {
    setInputs({[question]: value});
  };

  return (
    <>
      <BackAndNextSlide onNextSlide={handleNextSlide} isActiveNextSlide={isNextSlide} />
      <PopupLayout>
        <TitlePopup>{QUESTIONS.MORE_INFORMATION.breastFeedingOptions}</TitlePopup>
        <Stack direction="row" my={4}>
          {DATA_CHECK_BOX.map(val => (
            <CustomRadio
              style={{marginRight: 40, cursor: 'pointer'}}
              key={val.label}
              text={val.label}
              checked={inputs.breastFeedingOptions === val.label}
              onClick={() => handleClickOptions(val.label, 'breastFeedingOptions')}
            />
          ))}
        </Stack>
        <TitlePopup>{QUESTIONS.MORE_INFORMATION.smokeOptions}</TitlePopup>
        <Stack direction="row" my={4}>
          {DATA_CHECK_BOX.map(val => (
            <CustomRadio
              style={{marginRight: 40, cursor: 'pointer'}}
              key={val.label}
              text={val.label}
              checked={inputs.smokeOptions === val.label}
              onClick={() => handleClickOptions(val.label, 'smokeOptions')}
            />
          ))}
        </Stack>
        {inputs.smokeOptions === YES_OPTION && (
          <>
            <TitlePopup>{QUESTIONS.MORE_INFORMATION.oftenSmoke}</TitlePopup>
            <Stack direction="column" my={4}>
              {DATA_SELECT.map(val => (
                <CustomRadio
                  style={{alignItems: 'center', marginBottom: 10, cursor: 'pointer'}}
                  key={`${val.label}`}
                  text={val.label}
                  checked={inputs.oftenSmoke === val.label}
                  onClick={() => handleClickOptions(val.label, 'oftenSmoke')}
                  direction="row"
                />
              ))}
            </Stack>
          </>
        )}
      </PopupLayout>
    </>
  );
};
